/**
 * Helpers for turning an axios error from our API into something a user can
 * act on.
 *
 * The API answers a failed write with:
 *   { error, code?, message?, fields?: [{ path, kind, message }] }
 *
 * `fields` is what makes the difference between "Validation Error" and
 * "Ana Grup: bu alan zorunludur" — always prefer it when present.
 */

/**
 * Field-level failures keyed by form field name: { mainGroupId: 'required' }.
 * Empty object when the server did not say which fields were at fault.
 */
export function getErrorFields(err) {
  const fields = err?.response?.data?.fields;
  if (!Array.isArray(fields)) return {};
  return fields.reduce((acc, f) => {
    if (f?.path) acc[f.path] = f.kind || 'invalid';
    return acc;
  }, {});
}

/**
 * A human message for the toast.
 *
 * `labelFor(path)` maps a field path to its on-screen label; when it returns
 * nothing for every field we fall back to the server's own message rather than
 * showing a list of raw schema paths.
 */
export function getErrorMessage(err, { t, labelFor, fallback } = {}) {
  const data = err?.response?.data;
  if (!data) return fallback || '';

  const fields = Array.isArray(data.fields) ? data.fields : [];
  if (fields.length && typeof labelFor === 'function' && t) {
    const labelled = fields.map((f) => labelFor(f.path)).filter(Boolean);
    if (labelled.length) {
      const reason = fields.every((f) => f.kind === 'required')
        ? t('adminPanel.validation.requiredFields', { fields: labelled.join(', ') })
        : fields.every((f) => f.kind === 'duplicate')
          ? t('adminPanel.validation.duplicateFields', { fields: labelled.join(', ') })
          : t('adminPanel.validation.invalidFields', { fields: labelled.join(', ') });
      return reason;
    }
  }

  // No field info — the server message is still better than a bare error name.
  return data.message || data.error || fallback || '';
}
